/**
 * GameState - arbitro da partida (secao 4.19 do ARCHITECTURE.md).
 *
 * Transforma os sistemas soltos em um jogo com comeco, meio e fim:
 *   - maquina de estados 'menu' | 'playing' | 'paused' | 'gameover' | 'victory'
 *   - ondas de inimigos com intervalo, aviso e contagem regressiva
 *   - spawn seguro (longe e fora da visao do jogador)
 *   - pontuacao por baixa e por headshot
 *   - morte e respawn do jogador reiniciando a onda atual
 *   - vitoria ao concluir a onda 10
 *   - pausa por ESC (libera o ponteiro) e reinicio limpo sem recarregar a pagina
 *
 * Principios seguidos aqui:
 *   1. Este modulo NUNCA importa outro sistema: le tudo de ctx e usa optional
 *      chaining, porque qualquer vizinho pode ainda ser um stub.
 *   2. Nada lanca excecao dentro do laco de frame - tudo o que toca sistema
 *      alheio esta protegido e degrada em silencio com um unico console.warn.
 *   3. Zero alocacao por frame. Vetores sao temporarios de modulo. Os unicos
 *      objetos criados em tempo de execucao nascem em eventos raros (inicio de
 *      onda e spawn de bot), nunca no caminho quente.
 */

import * as THREE from 'three';
import { GAME, PLAYER, TEAM } from '../core/Constants.js';

// --- temporarios de modulo (nenhuma alocacao dentro de update) --------------
const _eye = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _pt = new THREE.Vector3();       // candidato sendo avaliado
const _best = new THREE.Vector3();     // melhor ponto de spawn de inimigo
const _pick = new THREE.Vector3();     // melhor ponto de spawn do jogador
const _toPoint = new THREE.Vector3();
const _probe = new THREE.Vector3();
const _losDir = new THREE.Vector3();
const _botPos = new THREE.Vector3();
const _center = new THREE.Vector3();

// --- ajuste de ritmo (o que nao esta em Constants.js por ser so daqui) ------
const MAX_WAVES = 10;               // vitoria ao concluir esta onda
const FIRST_WAVE_PREP = 4.0;        // preparo antes da onda 1 (nao e "intervalo entre ondas")
const RESTART_WAVE_BREAK = GAME.WAVE_BREAK * 0.6; // pausa menor ao refazer a onda apos morrer
const SPAWN_INTERVAL = 0.9;         // segundos entre spawns quando ja ha pressao em campo
const SPAWN_BURST_INTERVAL = 0.28;  // no comeco da onda o campo enche mais rapido
const SPAWN_BURST_ALIVE = 4;        // ate esta quantidade viva, usa o intervalo curto
const PREFERRED_SPAWN_DIST = 32;    // distancia "gostosa" de spawn: perto o bastante para
                                    // o inimigo chegar, longe o bastante para nao aparecer no colo
const MIN_SPAWN_DIST = 22;          // abaixo disso so em modo relaxado
const HARD_MIN_SPAWN_DIST = 14;     // limite absoluto: nunca spawna mais perto que isso
const SPAWN_POINT_COOLDOWN = 7.0;   // evita fila de inimigos saindo do mesmo canto
const VIEW_MARGIN_DEG = 16;         // folga alem do FOV real ao decidir "esta na visao"
const SPAWN_PATIENCE = 5.0;         // apos tanto tempo sem ponto valido, relaxa os criterios
const SPAWN_DESPERATE = 12.0;       // apos tanto tempo travado, deixa o BotManager escolher
const CLEAR_GRACE = 0.6;            // espera antes de declarar a onda limpa (spawn pode atrasar)
const BOT_CHEST_HEIGHT = 1.45;      // altura usada no teste de visibilidade do spawn
const LOCK_RETRY_DELAY = 0.75;      // nova tentativa de pointer lock ao retomar

const DEG2RAD = Math.PI / 180;

/** Le um ponto que pode vir como Vector3, array [x,y,z] ou objeto com .position. */
function readPoint(p, out) {
  if (!p) return false;
  if (typeof p.x === 'number' && typeof p.z === 'number') {
    out.set(p.x, typeof p.y === 'number' ? p.y : 0, p.z);
    return true;
  }
  if (Array.isArray(p) && p.length >= 3) {
    out.set(+p[0] || 0, +p[1] || 0, +p[2] || 0);
    return true;
  }
  if (p.position && typeof p.position.x === 'number') {
    out.set(p.position.x, p.position.y || 0, p.position.z);
    return true;
  }
  return false;
}

/** Posicao de uma entidade qualquer (bot), sem assumir a forma do objeto. */
function entityPosition(e, out) {
  if (!e) return false;
  if (e.position && typeof e.position.x === 'number') { out.copy(e.position); return true; }
  if (e.root?.position) { out.copy(e.root.position); return true; }
  if (e.mesh?.position) { out.copy(e.mesh.position); return true; }
  if (e.object?.position) { out.copy(e.object.position); return true; }
  if (e.model?.position) { out.copy(e.model.position); return true; }
  return false;
}

function lerp(a, b, t) { return a + (b - a) * t; }
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

export class GameState {
  constructor(ctx) {
    this.ctx = ctx;

    // O Engine so chama update() de sistemas com esta flag enquanto pausado.
    // Precisamos dela para conseguir DESPAUSAR (e so para isso: a logica de
    // onda checa engine.paused e nao anda com o jogo parado).
    this.updateWhilePaused = true;

    // --- estado publico (o HUD, o Menu e o DebugApi leem daqui) ---
    this.state = 'menu';
    this.phase = 'idle';          // 'idle' | 'intermission' | 'active' | 'clear'
    this.wave = 0;                // onda corrente (ja aponta para a que vem no intervalo)
    this.nextWave = 1;
    this.maxWaves = MAX_WAVES;
    this.score = 0;
    this.kills = 0;
    this.headshots = 0;
    this.waveTotal = 0;           // inimigos previstos para a onda atual
    this.enemiesLeft = 0;         // quantos ainda precisam morrer nesta onda
    this.enemiesRemaining = 0;    // alias (nomes diferentes de HUD)
    this.enemiesAlive = 0;        // quantos estao em campo agora
    this.countdown = 0;           // segundos ate a proxima onda (0 fora do intervalo)
    this.respawnIn = 0;           // segundos ate voltar a jogar (0 fora do gameover)

    // --- controle interno ---
    this._offs = [];              // funcoes de remocao dos ouvintes (dispose)
    this._timer = 0;              // cronometro da fase corrente
    this._nextWave = 1;
    this._spawnedThisWave = 0;
    this._killedThisWave = 0;
    this._spawnTimer = 0;
    this._sinceLastSpawn = 0;
    this._spawnBlocked = 0;       // tempo acumulado sem ponto de spawn valido
    this._lastSecond = -1;        // ultima contagem regressiva anunciada
    this._suppressScoreUntil = -1;// janela em que mortes sao administrativas (killAll)
    this._difficulty = null;      // objeto recriado uma vez por onda

    this._enemyPoints = null;
    this._playerPoints = null;
    this._pointCooldown = null;
    this._spawnsReady = false;

    this._escLatch = false;       // ESC pendente, consumido no update
    this._pausedFrom = 'playing'; // estado para o qual a pausa volta
    this._wasLocked = false;
    this._lockRetry = -1;
    this._emitting = false;       // evita eco do proprio GAME_STATE
    this._warned = new Set();

    this._onKeyDown = null;

    // objeto reaproveitado por getStatus() - sem alocacao por consulta
    this._status = {
      state: 'menu', phase: 'idle', wave: 0, nextWave: 1, maxWaves: MAX_WAVES,
      score: 0, kills: 0, headshots: 0, waveTotal: 0, enemiesLeft: 0,
      enemiesAlive: 0, countdown: 0, respawnIn: 0
    };
  }

  // =========================================================================
  // ciclo de vida
  // =========================================================================

  async init() {
    const { events, EVENTS } = this.ctx;

    // Ouvintes registrados UMA unica vez. restart() nao reassina nada - e por
    // isso que reiniciar a partida nao vaza listener.
    if (events) {
      this._offs.push(events.on(EVENTS.ENTITY_KILLED, (p) => this._onEntityKilled(p)));
      this._offs.push(events.on(EVENTS.PLAYER_DIED, (p) => this._onPlayerDied(p)));
      this._offs.push(events.on(EVENTS.GAME_STATE, (p) => this._onExternalState(p)));
    }

    // Ouvinte proprio de ESC. Motivo de nao usar input.wasPressed('pause'):
    // com o Engine pausado ele nao chama input.endFrame(), entao a borda de
    // subida nunca seria limpa e o jogo ficaria piscando entre pausa e jogo.
    // Aqui a borda e nossa e sobrevive a pausa, ao pointer lock e a input.enabled.
    this._onKeyDown = (e) => {
      if (e.repeat) return;
      const codes = this.ctx.input?.bindings?.pause;
      const isPause = Array.isArray(codes) ? codes.indexOf(e.code) >= 0 : e.code === 'Escape';
      if (isPause) this._escLatch = true;
    };
    window.addEventListener('keydown', this._onKeyDown);

    this._resolveLevelData();
    this._syncPublic();
  }

  dispose() {
    for (const off of this._offs) { try { off?.(); } catch { /* ignorado */ } }
    this._offs.length = 0;
    if (this._onKeyDown) window.removeEventListener('keydown', this._onKeyDown);
    this._onKeyDown = null;
  }

  // =========================================================================
  // API publica (Menu, HUD e DebugApi chamam isto)
  // =========================================================================

  /** Comeca a partida. Em 'paused' apenas retoma; em 'playing' nao faz nada. */
  start() {
    if (this.state === 'playing') return true;
    if (this.state === 'paused') { this.resume(); return true; }
    this.restart();
    return true;
  }

  /**
   * Reinicio total sem recarregar a pagina: limpa bots, decalques e particulas,
   * zera pontuacao, cura e reposiciona o jogador e volta para a onda 1.
   */
  restart() {
    this.score = 0;
    this.kills = 0;
    this.headshots = 0;
    this.wave = 0;
    this._killedThisWave = 0;
    this._spawnedThisWave = 0;
    this.waveTotal = 0;
    this._lastSecond = -1;
    this._spawnBlocked = 0;
    this.respawnIn = 0;

    this._clearBots();
    this._clearFx();
    this._resolveLevelData();
    this._resetPointCooldowns();
    this._revivePlayer();

    if (this.ctx.engine) this.ctx.engine.paused = false;
    this._escLatch = false;

    this._setState('playing');
    this._tryLock();
    this._beginIntermission(1, FIRST_WAVE_PREP);
    this._syncPublic();
    return true;
  }

  /**
   * Pausa. Vale durante a partida e tambem na tela de morte (a contagem de
   * respawn congela junto, porque o mundo inteiro para).
   */
  pause() {
    if (this.state !== 'playing' && this.state !== 'gameover') return false;
    this._escLatch = false;              // quem chamou primeiro ganha (Menu ou nos)
    this._pausedFrom = this.state;
    if (this.ctx.engine) this.ctx.engine.paused = true;
    try { this.ctx.input?.releaseLock?.(); } catch { /* ignorado */ }
    this._wasLocked = false;
    this._setState('paused');
    this._syncPublic();
    return true;
  }

  resume() {
    if (this.state !== 'paused') return false;
    this._escLatch = false;
    if (this.ctx.engine) this.ctx.engine.paused = false;
    this._setState(this._pausedFrom === 'gameover' ? 'gameover' : 'playing');
    this._tryLock();
    this._lockRetry = LOCK_RETRY_DELAY;  // pointer lock pode ser negado logo apos o ESC
    this._syncPublic();
    return true;
  }

  togglePause() {
    if (this.state === 'paused') return this.resume();
    return this.pause();
  }

  /** Abandona a partida e volta para o menu (usado pelo botao "sair" do Menu). */
  toMenu() {
    this._clearBots();
    this.phase = 'idle';
    this._timer = 0;
    this.countdown = 0;
    this.respawnIn = 0;
    if (this.ctx.engine) this.ctx.engine.paused = false;
    try { this.ctx.input?.releaseLock?.(); } catch { /* ignorado */ }
    this._wasLocked = false;
    this._setState('menu');
    this._syncPublic();
    return true;
  }

  /** Estado compacto para a interface. Devolve SEMPRE o mesmo objeto (sem lixo). */
  getStatus() {
    const s = this._status;
    s.state = this.state; s.phase = this.phase; s.wave = this.wave;
    s.nextWave = this.nextWave;
    s.maxWaves = this.maxWaves; s.score = this.score; s.kills = this.kills;
    s.headshots = this.headshots; s.waveTotal = this.waveTotal;
    s.enemiesLeft = this.enemiesLeft; s.enemiesAlive = this.enemiesAlive;
    s.countdown = this.countdown; s.respawnIn = this.respawnIn;
    return s;
  }

  /** Quantos inimigos ainda faltam nesta onda (usado pelo HUD). */
  getEnemiesLeft() { return this.enemiesLeft; }

  /** Numero de inimigos previstos para uma onda. Formula do contrato. */
  waveSize(n) {
    const total = Math.round(GAME.FIRST_WAVE_ENEMIES * Math.pow(GAME.ENEMIES_GROWTH, Math.max(0, n - 1)));
    return Math.max(1, total);
  }

  // =========================================================================
  // laco
  // =========================================================================

  update(dt) {
    // Regra 8 do contrato: este modulo nunca derruba o frame.
    try {
      // Partida ONLINE em andamento: a campanha sai de cena por inteiro. Sem esta
      // guarda, o GAME_STATE 'playing' emitido pelo MatchState faria as ondas PvE
      // rodarem POR CIMA do deathmatch - bots do modo campanha nascendo no meio
      // de uma partida entre jogadores. Os dois modos sao irmaos, nao empilhaveis.
      if (this.ctx.match?.isActive?.()) return;

      this._pollPauseInput();

      // Pausado: nada do mundo avanca. So a deteccao de ESC acima roda.
      if (this.ctx.engine?.paused) { this._syncPublic(); return; }

      if (this._lockRetry > 0) {
        this._lockRetry -= dt;
        if (this._lockRetry <= 0 && this.state === 'playing' && !this.ctx.input?.locked) this._tryLock();
      }

      if (this.state === 'playing') this._updatePlaying(dt);
      else if (this.state === 'gameover') this._updateGameOver(dt);

      this._syncPublic();
    } catch (err) {
      this._warnOnce('update', err);
    }
  }

  _updatePlaying(dt) {
    if (this._pointCooldown) {
      for (let i = 0; i < this._pointCooldown.length; i++) {
        if (this._pointCooldown[i] > 0) this._pointCooldown[i] -= dt;
      }
    }

    if (this.phase === 'intermission') {
      this._timer -= dt;
      this.countdown = Math.max(0, this._timer);

      // Contagem regressiva nos ultimos 5 segundos, um aviso por segundo.
      const sec = Math.ceil(this._timer);
      if (sec !== this._lastSecond && sec > 0 && sec <= 5) {
        this._lastSecond = sec;
        this._notice(`ONDA ${this._nextWave} EM ${sec}`, 1.05);
      }
      if (this._timer <= 0) this._beginWave(this._nextWave);
      return;
    }

    if (this.phase !== 'active') return;

    this._sinceLastSpawn += dt;
    this._pumpSpawns(dt);

    // Onda concluida: tudo que devia nascer nasceu e nao ha mais ninguem vivo.
    // A carencia evita declarar vitoria no intervalo entre pedir o spawn e o
    // BotManager de fato contabilizar o bot.
    const alive = this._aliveEnemies();
    if (this._spawnedThisWave >= this.waveTotal && alive <= 0 && this._sinceLastSpawn > CLEAR_GRACE) {
      this._finishWave();
    }
  }

  _updateGameOver(dt) {
    this._timer -= dt;
    this.respawnIn = Math.max(0, this._timer);

    const sec = Math.ceil(this._timer);
    if (sec !== this._lastSecond && sec > 0) {
      this._lastSecond = sec;
      this._notice(`RESPAWN EM ${sec}`, 1.05);
    }
    if (this._timer <= 0) this._respawnPlayer();
  }

  // =========================================================================
  // ondas
  // =========================================================================

  /**
   * Intervalo entre ondas. `this.wave` ja passa a valer a onda que vem, para o
   * HUD e a contagem regressiva falarem do mesmo numero durante a espera.
   */
  _beginIntermission(waveNumber, duration, leadText) {
    const first = this.wave === 0;
    this._nextWave = waveNumber;
    this.wave = waveNumber;
    this.nextWave = waveNumber;
    this.phase = 'intermission';
    this._timer = Math.max(0.1, duration);
    this.countdown = this._timer;
    this._lastSecond = -1;
    this.waveTotal = this.waveSize(waveNumber);
    this.enemiesLeft = this.waveTotal;
    this._killedThisWave = 0;
    this._spawnedThisWave = 0;
    this._spawnTimer = 0;
    this._sinceLastSpawn = 0;
    this._spawnBlocked = 0;

    const lead = leadText || (first
      ? `PREPARE-SE - ONDA 1 DE ${MAX_WAVES}`
      : `PROXIMA ONDA: ${waveNumber} DE ${MAX_WAVES}`);
    this._notice(lead, 2.4);
  }

  _beginWave(n) {
    this.wave = n;
    this.nextWave = n;
    this.phase = 'active';
    this.waveTotal = this.waveSize(n);
    this._killedThisWave = 0;
    this._spawnedThisWave = 0;
    this._spawnTimer = 0;
    this._sinceLastSpawn = 0;
    this._spawnBlocked = 0;
    this._lastSecond = -1;
    this.countdown = 0;
    this.enemiesLeft = this.waveTotal;

    this._resetPointCooldowns();
    this._difficulty = this._buildDifficulty(n);
    this._applyDifficulty(this._difficulty);

    this._emit(this.ctx.EVENTS.WAVE_START, { wave: n, enemies: this.waveTotal });
    this._notice(`ONDA ${n} - ${this.waveTotal} HOSTIS`, 2.6);
    this._sfx('wave_start');
  }

  _finishWave() {
    this.phase = 'clear';
    this.enemiesLeft = 0;
    this._emit(this.ctx.EVENTS.WAVE_CLEAR, { wave: this.wave });
    this._notice(`ONDA ${this.wave} NEUTRALIZADA`, 2.6);
    this._sfx('wave_clear');

    if (this.wave >= MAX_WAVES) { this._victory(); return; }
    this._beginIntermission(this.wave + 1, GAME.WAVE_BREAK);
  }

  _victory() {
    this.phase = 'idle';
    this.countdown = 0;
    this._clearBots();
    try { this.ctx.input?.releaseLock?.(); } catch { /* ignorado */ }
    this._wasLocked = false;
    this._setState('victory');
    this._notice(`OPERACAO CONCLUIDA - ${this.score} PONTOS`, 8);
    this._sfx('victory');
  }

  /**
   * Alimenta o campo de batalha aos poucos, respeitando GAME.MAX_ALIVE_ENEMIES.
   * Repoe conforme os inimigos morrem, ate cumprir a cota da onda.
   */
  _pumpSpawns(dt) {
    if (this._spawnedThisWave >= this.waveTotal) return;

    const alive = this._aliveEnemies();
    if (alive >= GAME.MAX_ALIVE_ENEMIES) { this._spawnTimer = 0; return; }

    this._spawnTimer -= dt;
    if (this._spawnTimer > 0) return;

    const point = this._pickEnemySpawn(this._spawnBlocked >= SPAWN_PATIENCE);
    if (!point) {
      this._spawnBlocked += dt;
      // Valvula de escape: se nem relaxando aparece ponto (cenario minusculo,
      // lista de spawn ruim), deixa o BotManager escolher sozinho. Uma onda
      // travada para sempre e pior do que um spawn menos elegante.
      if (this._spawnBlocked >= SPAWN_DESPERATE && this._spawnBot(null)) {
        this._spawnedThisWave++;
        this._sinceLastSpawn = 0;
        this._spawnBlocked = 0;
        this._spawnTimer = SPAWN_INTERVAL;
        return;
      }
      // Sem lugar seguro agora (jogador varrendo o mapa com o olhar, por exemplo).
      // Espera o proximo tique em vez de nascer inimigo na cara dele.
      this._spawnTimer = 0.25;
      return;
    }

    this._spawnBlocked = 0;
    if (this._spawnBot(point)) {
      this._spawnedThisWave++;
      this._sinceLastSpawn = 0;
      this._spawnTimer = alive < SPAWN_BURST_ALIVE ? SPAWN_BURST_INTERVAL : SPAWN_INTERVAL;
    } else {
      // BotManager indisponivel ou recusou: tenta de novo daqui a pouco sem
      // travar a onda para sempre.
      this._spawnTimer = 1.0;
    }
  }

  /**
   * Escolhe um ponto de spawn: longe, fora do campo de visao e sem linha de
   * visao a partir do olho do jogador. Inimigo nascendo na frente do jogador e
   * falha grave, entao o padrao e recusar em vez de arriscar; o modo relaxado
   * so entra depois de SPAWN_PATIENCE segundos travado.
   */
  _pickEnemySpawn(relaxed) {
    const points = this._enemyPoints;
    if (!points || points.length === 0) return null;

    this._playerEye(_eye);
    this._cameraForward(_fwd);
    const cosHalf = this._viewCosHalfAngle();

    let bestScore = -Infinity;
    let bestIndex = -1;

    for (let i = 0; i < points.length; i++) {
      // No modo relaxado o cooldown tambem cai: melhor repetir o ponto do que
      // travar a onda em cenario com poucos spawns.
      if (!relaxed && this._pointCooldown && this._pointCooldown[i] > 0) continue;
      if (!readPoint(points[i], _pt)) continue;

      const dist = _pt.distanceTo(_eye);
      if (dist < HARD_MIN_SPAWN_DIST) continue;
      if (!relaxed && dist < MIN_SPAWN_DIST) continue;

      const visible = this._isSpawnVisible(_pt, _eye, _fwd, cosHalf, dist);
      if (visible && !relaxed) continue;

      // Banda doce de distancia + forte preferencia por ficar fora da visao +
      // bonus por estar atras do jogador. O ruido evita que a onda inteira
      // saia sempre do mesmo canto.
      let score = -Math.abs(dist - PREFERRED_SPAWN_DIST) * 0.6;
      score += visible ? -80 : 40;
      _toPoint.subVectors(_pt, _eye);
      _toPoint.y = 0;
      if (_toPoint.lengthSq() > 1e-6) {
        _toPoint.normalize();
        score += (1 - _toPoint.dot(_fwd)) * 7; // 0 na frente, ~14 atras
      }
      score += Math.random() * 6;

      if (score > bestScore) { bestScore = score; bestIndex = i; _best.copy(_pt); }
    }

    if (bestIndex < 0) return null;
    if (this._pointCooldown) this._pointCooldown[bestIndex] = SPAWN_POINT_COOLDOWN;
    return _best;
  }

  /** Visivel = dentro do cone de visao E com linha livre ate o corpo do bot. */
  _isSpawnVisible(point, eye, fwd, cosHalf, dist) {
    if (dist < 1e-3) return true;
    _toPoint.subVectors(point, eye).divideScalar(dist);
    if (_toPoint.dot(fwd) < cosHalf) return false;   // esta fora do enquadramento
    _probe.copy(point); _probe.y += BOT_CHEST_HEIGHT;
    if (this._lineOfSight(eye, _probe)) return true;
    _probe.y = point.y + 0.35;
    return this._lineOfSight(eye, _probe);
  }

  _lineOfSight(a, b) {
    const col = this.ctx.collision;
    try {
      if (typeof col?.lineOfSight === 'function') return !!col.lineOfSight(a, b);
      if (typeof col?.raycast === 'function') {
        _losDir.subVectors(b, a);
        const d = _losDir.length();
        if (d < 1e-4) return true;
        _losDir.divideScalar(d);
        return !col.raycast(a, _losDir, d - 0.15);   // null = caminho livre
      }
    } catch (err) {
      this._warnOnce('lineOfSight', err);
    }
    // Sem sistema de colisao util: assume visivel (conservador - empurra o
    // spawn para tras do jogador em vez de arriscar nascer na frente).
    return true;
  }

  /**
   * Pede um bot ao BotManager na posicao escolhida. O contrato so garante
   * spawn(n); tentamos as formas mais especificas primeiro e caimos para a
   * generica. A posicao vai como Vector3 NOVO (o BotManager pode guardar a
   * referencia - passar um temporario de modulo criaria aliasing).
   */
  _spawnBot(point) {
    const bots = this.ctx.bots;
    if (!bots) return false;

    const position = point ? new THREE.Vector3(point.x, point.y, point.z) : null;
    const opts = {
      position,
      point: position,
      team: TEAM.ENEMY,
      wave: this.wave,
      difficulty: this._difficulty
    };

    try {
      if (position && typeof bots.spawnAt === 'function') { bots.spawnAt(position, opts); return true; }
      if (position && typeof bots.spawnOne === 'function') { bots.spawnOne(position, opts); return true; }
      if (typeof bots.spawn === 'function') {
        // Implementacoes que so aceitam (n) simplesmente ignoram o 2o argumento;
        // nesse caso o BotManager escolhe o ponto por conta propria.
        bots.spawn(1, opts);
        return true;
      }
    } catch (err) {
      this._warnOnce('spawn', err);
    }
    return false;
  }

  // =========================================================================
  // dificuldade progressiva
  // =========================================================================

  /**
   * Curva discreta: nas ondas altas o bot mira um pouco melhor, reage mais
   * rapido e recua menos. Criado UMA vez por onda (nao e caminho quente).
   */
  _buildDifficulty(n) {
    const t = clamp01((n - 1) / Math.max(1, MAX_WAVES - 1));
    return {
      wave: n,
      level: t,
      accuracy: lerp(0.62, 1.0, t),      // multiplicador de precisao
      aimTime: lerp(1.18, 0.74, t),      // multiplicador do tempo de mira (menor = mira antes)
      reaction: lerp(1.15, 0.76, t),     // multiplicador do tempo de reacao
      aggression: lerp(0.55, 1.0, t),    // recua menos e avanca mais
      burst: lerp(0.85, 1.25, t),        // rajadas um pouco mais longas
      health: lerp(1.0, 1.15, t)
    };
  }

  _applyDifficulty(d) {
    const bots = this.ctx.bots;
    if (!bots || !d) return;
    try {
      if (typeof bots.setDifficulty === 'function') { bots.setDifficulty(d); return; }
      // Sem setter no contrato: deixamos o parametro em um campo de dados para
      // quem quiser consumir. Se ninguem consumir, nada quebra.
      if (typeof bots.difficulty !== 'function') bots.difficulty = d;
    } catch (err) {
      this._warnOnce('difficulty', err);
    }
  }

  // =========================================================================
  // jogador: morte, respawn e reinicio
  // =========================================================================

  _onPlayerDied() {
    if (this.state !== 'playing') return;
    this.phase = 'idle';
    this._timer = GAME.RESPAWN_DELAY;
    this.respawnIn = GAME.RESPAWN_DELAY;
    this._lastSecond = -1;
    this.countdown = 0;

    // Cadaver nao anda. O olhar continua livre (camera de morte).
    if (this.ctx.controller) this.ctx.controller.enabled = false;

    this._setState('gameover');
    this._notice('VOCE CAIU', 1.4);
    this._sfx('death');
  }

  /**
   * Volta ao jogo reiniciando a ONDA ATUAL do zero: limpa o campo, cura,
   * recarrega e reposiciona longe dos inimigos. A pontuacao acumulada e
   * mantida - o castigo e perder o progresso da onda, nao a partida inteira.
   */
  _respawnPlayer() {
    this.respawnIn = 0;
    this._lastSecond = -1;
    this._clearBots();
    this._clearFx();
    this._resetPointCooldowns();
    this._revivePlayer();

    const wave = Math.max(1, this.wave);
    this._setState('playing');
    this._beginIntermission(wave, RESTART_WAVE_BREAK, `ONDA ${wave} REINICIADA`);
  }

  /** Cura, revive, reposiciona e recarrega. Usado no restart e no respawn. */
  _revivePlayer() {
    const { player, controller, cameraRig, weapons } = this.ctx;

    try {
      if (typeof player?.reset === 'function') player.reset();
      if (player && player.alive === false) player.alive = true;
      const max = player?.maxHealth ?? PLAYER.MAX_HEALTH;
      if (player && (player.health ?? 0) < max) {
        if (typeof player.heal === 'function') player.heal(max);
        else player.health = max;
      }
      if (player) player.score = this.score;
    } catch (err) {
      this._warnOnce('revive', err);
    }

    // Reposiciona longe dos inimigos, em ponto de spawn do jogador.
    try {
      if (this._pickPlayerSpawn(_pick)) {
        controller?.setPosition?.(_pick.x, _pick.y + 0.05, _pick.z);
        if (controller?.velocity?.set) controller.velocity.set(0, 0, 0);

        // Olhar para o miolo do mapa em vez de para o muro atras.
        const yaw = this._yawTowardsCenter(_pick);
        const eye = new THREE.Vector3(_pick.x, _pick.y + (PLAYER.EYE_HEIGHT || 1.65), _pick.z);
        if (typeof cameraRig?.setPose === 'function') cameraRig.setPose(eye, yaw, 0);
        else if (this.ctx.camera) {
          this.ctx.camera.position.copy(eye);
          this.ctx.camera.rotation.set(0, yaw, 0, 'YXZ');
        }
      }
    } catch (err) {
      this._warnOnce('reposition', err);
    }

    if (controller) controller.enabled = true;

    // Recarga total. O contrato do WeaponSystem nao define um metodo de
    // reabastecimento, entao tentamos os nomes plausiveis sem depender de nenhum.
    try {
      const w = weapons;
      if (typeof w?.resetAmmo === 'function') w.resetAmmo();
      else if (typeof w?.refill === 'function') w.refill();
      else if (typeof w?.refillAmmo === 'function') w.refillAmmo();
      else if (typeof w?.reset === 'function') w.reset();
    } catch (err) {
      this._warnOnce('rearm', err);
    }
  }

  /**
   * Ponto de spawn do jogador que maximiza a distancia ao inimigo mais proximo.
   * `out` NAO pode ser o mesmo temporario usado na iteracao (_pt) - por isso o
   * chamador passa _pick.
   */
  _pickPlayerSpawn(out) {
    const points = this._playerPoints;
    if (!points || points.length === 0) return false;

    const bots = this._botList();
    let bestScore = -Infinity;
    let found = false;

    for (let i = 0; i < points.length; i++) {
      if (!readPoint(points[i], _pt)) continue;

      let nearest = Infinity;
      if (bots) {
        for (let b = 0; b < bots.length; b++) {
          if (!entityPosition(bots[b], _botPos)) continue;
          const d = _botPos.distanceTo(_pt);
          if (d < nearest) nearest = d;
        }
      }
      // Sem bots vivos: qualquer ponto serve, decide o sorteio.
      const score = (Number.isFinite(nearest) ? Math.min(nearest, 60) : 30) + Math.random() * 8;
      if (score > bestScore) { bestScore = score; out.copy(_pt); found = true; }
    }

    if (!found) return false;

    // Cola no chao real, caso a lista traga altura aproximada.
    try {
      const g = this.ctx.collision?.sampleGround?.(out.x, out.z, 50);
      if (g && Number.isFinite(g.y) && Math.abs(g.y - out.y) < 6) out.y = g.y;
    } catch (err) {
      this._warnOnce('sampleGround', err);
    }
    return true;
  }

  _yawTowardsCenter(from) {
    let cx = 0, cz = 0;
    const b = this.ctx.level?.bounds;
    if (b && typeof b.getCenter === 'function') {
      b.getCenter(_center);
      cx = _center.x; cz = _center.z;
    }
    const dx = cx - from.x;
    const dz = cz - from.z;
    if (Math.abs(dx) < 1e-4 && Math.abs(dz) < 1e-4) return 0;
    // Camera olha para -Z: forward(yaw) = (-sin yaw, 0, -cos yaw).
    return Math.atan2(-dx, -dz);
  }

  // =========================================================================
  // pontuacao
  // =========================================================================

  _onEntityKilled(p) {
    const e = p?.entity;
    if (!e) return;
    if (this._isPlayerEntity(e)) return;         // a morte do jogador tem fluxo proprio

    // Limpeza administrativa (killAll do restart/respawn) nao vale ponto nem baixa.
    const now = this.ctx.engine?.elapsed ?? 0;
    if (now < this._suppressScoreUntil) return;

    this.kills++;
    if (this.phase === 'active') this._killedThisWave++;

    const src = p.source;
    const killedByEnemy = !!(src && (src.team === TEAM.ENEMY || src.isBot === true));
    if (!killedByEnemy) {
      this.score += GAME.SCORE_KILL;
      if (p.headshot) {
        this.score += GAME.SCORE_HEADSHOT_BONUS;
        this.headshots++;
      }
      if (this.ctx.player) this.ctx.player.score = this.score;
    }
  }

  _isPlayerEntity(e) {
    const c = this.ctx;
    if (e === c.player || e === c.controller) return true;
    if (e.isPlayer === true) return true;
    if (e.team === TEAM.PLAYER && e.isBot !== true) return true;
    return false;
  }

  // =========================================================================
  // pausa e pointer lock
  // =========================================================================

  _pollPauseInput() {
    const input = this.ctx.input;

    // Perder o pointer lock durante a partida = usuario apertou ESC (o browser
    // consome esse ESC e nem sempre entrega o keydown) ou clicou fora. Pausar
    // e o comportamento esperado. So dispara na TRANSICAO de travado para
    // destravado, entao a revisao automatizada (que joga sem lock) nao pausa.
    const locked = !!input?.locked;
    const lockLost = this._wasLocked && !locked;
    this._wasLocked = locked;

    const esc = this._escLatch;
    this._escLatch = false;

    if (this.state === 'playing' || this.state === 'gameover') {
      if (esc || lockLost) this.pause();
    } else if (this.state === 'paused') {
      if (esc) this.resume();
    }
  }

  /**
   * Pede o pointer lock apenas quando ha ativacao do usuario recente. Sem essa
   * guarda, o start() vindo do DebugApi (sem clique) gera promessa rejeitada e
   * suja window.__GAME__.errors, que a revisao automatizada le.
   */
  _tryLock() {
    try {
      const input = this.ctx.input;
      if (!input || input.locked) return false;
      const ua = navigator.userActivation;
      if (ua && ua.isActive === false) return false;
      input.requestLock?.();
      return true;
    } catch (err) {
      this._warnOnce('pointerLock', err);
      return false;
    }
  }

  // =========================================================================
  // integracao com os vizinhos (tudo tolerante a stub)
  // =========================================================================

  _resolveLevelData() {
    if (this._spawnsReady) return true;
    const sp = this.ctx.level?.spawnPoints;
    const enemy = Array.isArray(sp?.enemy) && sp.enemy.length ? sp.enemy : null;
    const player = Array.isArray(sp?.player) && sp.player.length ? sp.player : null;
    if (!enemy && !player) return false;

    this._enemyPoints = enemy || player;
    this._playerPoints = player || enemy;
    this._pointCooldown = new Float32Array(this._enemyPoints.length);
    this._spawnsReady = true;
    return true;
  }

  _resetPointCooldowns() {
    if (this._pointCooldown) this._pointCooldown.fill(0);
  }

  _botCount() {
    try {
      const n = this.ctx.bots?.count?.();
      return typeof n === 'number' ? n : NaN;
    } catch (err) {
      this._warnOnce('botCount', err);
      return NaN;
    }
  }

  _botList() {
    try {
      const list = this.ctx.bots?.getBots?.();
      return Array.isArray(list) ? list : null;
    } catch (err) {
      this._warnOnce('getBots', err);
      return null;
    }
  }

  /**
   * Vivos em campo. Cruza duas fontes porque nenhuma e infalivel: count() pode
   * incluir cadaveres e a contagem por evento perde bot que morreu sem emitir.
   * O minimo entre as duas erra para o lado seguro nos dois casos.
   */
  _aliveEnemies() {
    const byEvents = Math.max(0, this._spawnedThisWave - this._killedThisWave);
    const byManager = this._botCount();
    if (!Number.isFinite(byManager)) return byEvents;
    return Math.min(byEvents, Math.max(0, byManager));
  }

  _clearBots() {
    // Janela em que ENTITY_KILLED nao vale ponto: a limpeza pode emitir mortes.
    this._suppressScoreUntil = (this.ctx.engine?.elapsed ?? 0) + 0.5;
    try { this.ctx.bots?.killAll?.(); } catch (err) { this._warnOnce('killAll', err); }
    this._spawnedThisWave = 0;
    this._killedThisWave = 0;
    this.enemiesAlive = 0;
  }

  _clearFx() {
    const fx = this.ctx.fx;
    if (!fx) return;
    try {
      if (typeof fx.clear === 'function') fx.clear();
      else if (typeof fx.reset === 'function') fx.reset();
      if (typeof fx.clearDecals === 'function') fx.clearDecals();
      else if (typeof fx.decals?.clear === 'function') fx.decals.clear();
    } catch (err) {
      this._warnOnce('clearFx', err);
    }
  }

  _playerEye(out) {
    const cam = this.ctx.camera;
    const ctrl = this.ctx.controller;
    if (cam && (cam.position.x !== 0 || cam.position.y !== 0 || cam.position.z !== 0)) {
      out.copy(cam.position);
      return out;
    }
    if (ctrl?.position) {
      out.copy(ctrl.position);
      out.y += ctrl.eyeHeight ?? PLAYER.EYE_HEIGHT;
      return out;
    }
    out.set(0, PLAYER.EYE_HEIGHT, 0);
    return out;
  }

  _cameraForward(out) {
    const cam = this.ctx.camera;
    if (cam) {
      cam.getWorldDirection(out);
      out.y = 0;
      if (out.lengthSq() > 1e-8) { out.normalize(); return out; }
    }
    out.set(0, 0, -1);
    return out;
  }

  /** Cosseno do semiangulo horizontal de visao, com folga. */
  _viewCosHalfAngle() {
    const cam = this.ctx.camera;
    const vFov = ((cam?.fov ?? this.ctx.settings?.fov ?? 80) * DEG2RAD) / 2;
    const aspect = cam?.aspect || 16 / 9;
    const hFov = Math.atan(Math.tan(vFov) * aspect);
    return Math.cos(Math.min(Math.PI * 0.98, hFov + VIEW_MARGIN_DEG * DEG2RAD));
  }

  // =========================================================================
  // eventos
  // =========================================================================

  _setState(next) {
    if (this.state === next) return;
    this.state = next;
    this._emitting = true;                 // impede que o proprio eco reentre
    this._emit(this.ctx.EVENTS.GAME_STATE, {
      state: next, wave: this.wave, score: this.score
    });
    this._emitting = false;
  }

  /**
   * GAME_STATE tambem chega de fora (main.js no boot, Menu ao clicar jogar,
   * DebugApi.start()). Tratamos como COMANDO, para o Menu funcionar tanto
   * chamando ctx.game.start() quanto so emitindo o evento.
   */
  _onExternalState(p) {
    if (this._emitting) return;
    // Mesmo motivo do update(): com partida online ativa, o 'playing' que chega
    // e do deathmatch e nao um pedido para comecar a campanha.
    if (this.ctx.match?.isActive?.()) return;
    const s = p?.state;
    if (!s || s === this.state) return;
    try {
      if (s === 'playing') {
        if (this.state === 'paused') this.resume();
        else if (this.state !== 'playing') this.start();
      } else if (s === 'paused') {
        this.pause();
      } else if (s === 'menu') {
        if (this.state !== 'menu') this.toMenu();
      }
      // 'gameover' e 'victory' so nascem aqui dentro: ignorados como comando.
    } catch (err) {
      this._warnOnce('externalState', err);
    }
  }

  _emit(type, payload) {
    try { this.ctx.events?.emit?.(type, payload); } catch (err) { this._warnOnce('emit', err); }
  }

  _notice(text, duration) {
    this._emit(this.ctx.EVENTS.NOTICE, { text, duration });
  }

  /**
   * Som de interface. Vai SO pelo evento SFX (caminho desacoplado do contrato) -
   * chamar ctx.audio.play() aqui tambem tocaria duas vezes se o AudioSystem
   * escutar o evento, que e o desenho previsto no EventBus.
   */
  _sfx(name) {
    this._emit(this.ctx.EVENTS.SFX, { name, volume: 1 });
  }

  _syncPublic() {
    this.enemiesAlive = this.phase === 'active' ? this._aliveEnemies() : 0;
    this.enemiesLeft = Math.max(0, this.waveTotal - this._killedThisWave);
    this.enemiesRemaining = this.enemiesLeft;
    if (this.state !== 'gameover') this.respawnIn = 0;
    if (this.phase !== 'intermission') this.countdown = 0;
  }

  _warnOnce(key, err) {
    if (this._warned.has(key)) return;
    this._warned.add(key);
    console.warn(`[GameState] falha em "${key}" - seguindo sem isso:`, err);
  }
}
